import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import _ from 'lodash';
import PageHeader from 'Components/PageHeader';
import { RoleService, DateService } from 'Services';
import { useTranslation } from 'react-i18next';
import useCubeReportQuery from 'Hooks/query-hooks/reports/useCubeReportQuery';
import { shouldShowMerchants } from 'Selectors';
import { updateCurrentReport, addAdditionalBreadcrumbParams } from 'Store';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import RulePerformanceReportView from 'Routes/Reports/components/RulePerformanceReportView';
import MerchantSummaryReportView from 'Routes/Reports/components/MerchantSummaryReportView';
import useSingleReport from 'Hooks/query-hooks/reports/useSingleReport';
import useMarkReportAsRead from 'Hooks/query-hooks/reports/useMarkReportAsRead';
import useReplaceMerchant from 'Hooks/useReplaceMerchant';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import appStyles from 'Routes/Routes.css';
import { getCubeReportQuery } from '../NewReport/components/NewReportForm/helpers';
import DeleteReport from './components/DeleteReport';
import ExportCSV from './components/ExportCSV';
import styles from './index.css';

type Props = {
    reportType: ReportType;
};

const SavedReport = ({ reportType }: Props) => {
    const { rid } = useParams<{ rid: string }>();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const recordId = parseInt(rid, 10);
    const location = useLocation();
    const { users } = useGetReduxUsers();
    const singleReportAPI = useSingleReport(recordId);
    const { hasError, errorCmp } = errorController([singleReportAPI.error]);
    const { mutate, isSuccess } = useMarkReportAsRead(recordId, reportType);
    const [cubeData, setCubeData] = React.useState();
    const [countQuery, setCountQuery] = React.useState({});
    const replacedMerchant = useReplaceMerchant(singleReportAPI.data?.properties?.merchant);
    const bcrumbText = shouldShowMerchants() ? replacedMerchant : t('View Report');
    useEffect(() => {
        if (singleReportAPI.isSuccess) {
            dispatch(updateCurrentReport(singleReportAPI.data));
            const { dateRangeLabel, dateRange } = singleReportAPI.data?.properties || {};
            const { dateRangeValue, dateRangeType } = dateRange || {};

            dispatch(
                addAdditionalBreadcrumbParams({
                    pathname: location.pathname,
                    params: {
                        Merchant: bcrumbText,
                        dateRangeLabel: DateService.getReportDateRange(dateRangeType, dateRangeValue, dateRangeLabel)
                    }
                })
            );
        }

        if (singleReportAPI.data?.properties?.modifiedFlag === 'new' && !isSuccess) mutate();
        if (reportType === 'performance' && singleReportAPI.data?.properties) {
            setCubeData(singleReportAPI.data?.properties);
            const query = getCubeReportQuery(singleReportAPI.data?.properties);
            setCountQuery(query);
        }
    }, [
        reportType,
        singleReportAPI.data,
        dispatch,
        isSuccess,
        location,
        mutate,
        bcrumbText,
        setCubeData,
        singleReportAPI.isSuccess
    ]);
    const { resultSet } = useCubeReportQuery(cubeData, countQuery);
    const reportCountData = resultSet?.tablePivot() || [];
    const merchantReportProperties: ProcessedNewMerchantReportFormValues = singleReportAPI.data?.properties;
    const performanceReportProperties: ProcessedPerformanceReportFormValues = singleReportAPI.data?.properties;
    const tLabelText = useReplaceMerchant(performanceReportProperties?.merchant);

    if (hasError) return <>{errorCmp}</>;

    if (singleReportAPI.isLoading || singleReportAPI.isFetching || _.isUndefined(singleReportAPI.data)) {
        return <RouteLoading />;
    }

    return (
        <div className={appStyles.landingPage}>
            <PageHeader className={styles.pageHeaderWrapper}>
                {reportType === 'merchantsummary' && (
                    <PageHeader.Left className={styles.titleText}>
                        {shouldShowMerchants() ? (
                            <>
                                {tLabelText} {t('Summary Report')}
                            </>
                        ) : (
                            <>{t('Transaction Summary Report')}</>
                        )}
                    </PageHeader.Left>
                )}
                {reportType === 'performance' && (
                    <PageHeader.Left className={styles.titleText}>
                        {shouldShowMerchants() ? (
                            <>
                                {tLabelText} {t('Performance Report')}
                            </>
                        ) : (
                            <>{t('Performance Report')}</>
                        )}
                    </PageHeader.Left>
                )}
                <PageHeader.Right>
                    <ExportCSV recordId={rid} reportProperties={merchantReportProperties} reportType={reportType} />
                    {RoleService.isReportsAdmin() && (
                        <DeleteReport activeReportData={singleReportAPI.data} reportType={reportType} />
                    )}
                </PageHeader.Right>
            </PageHeader>
            {reportType === 'merchantsummary' && (
                <MerchantSummaryReportView
                    formValues={merchantReportProperties}
                    createdReportInfo={{
                        createdAt: singleReportAPI.data?.createdAt,
                        createdBy: singleReportAPI.data?.createdBy
                    }}
                    users={users}
                />
            )}
            {reportType === 'performance' && (
                <RulePerformanceReportView
                    reportCountData={reportCountData}
                    formValues={performanceReportProperties}
                    createdReportInfo={{
                        createdAt: singleReportAPI.data?.createdAt,
                        createdBy: singleReportAPI.data?.createdBy
                    }}
                    users={users}
                />
            )}
        </div>
    );
};

export default SavedReport;
